// =============================================================================
// StatusAvatar – Avatar with presence/status indicator dot
// Statuses: available, busy, dnd, offline, absent
// =============================================================================

import * as React from 'react';
import { cn } from '@/lib/utils';
import { Avatar, AvatarImage, AvatarFallback } from '@/components/ui/avatar';
import { Tooltip, TooltipContent, TooltipTrigger } from '@/components/ui/tooltip';
import { Ban, X } from 'lucide-react';

export type UserStatus = 'available' | 'busy' | 'dnd' | 'offline' | 'absent';

interface StatusAvatarProps extends React.ComponentPropsWithoutRef<typeof Avatar> {
  src?: string | null;
  fallback?: string;
  status?: UserStatus;
  /** Hide the status dot entirely */
  hideStatus?: boolean;
  /** Size class for the avatar – defaults to 'md' */
  size?: 'sm' | 'md' | 'lg';
}

const statusLabels: Record<UserStatus, string> = {
  available: 'Disponible',
  busy: 'Occupé(e)',
  dnd: 'Ne pas déranger',
  offline: 'Hors ligne',
  absent: 'Absent(e)',
};

const sizeClasses = {
  sm: 'h-7 w-7',
  md: 'h-10 w-10',
  lg: 'h-12 w-12',
};

const dotSizeClasses = {
  sm: 'h-2.5 w-2.5 -bottom-0 -right-0',
  md: 'h-3 w-3 -bottom-0.5 -right-0.5',
  lg: 'h-3.5 w-3.5 -bottom-0.5 -right-0.5',
};

const dndIconSize = {
  sm: 'h-2 w-2',
  md: 'h-2.5 w-2.5',
  lg: 'h-3 w-3',
};

const StatusAvatar = React.forwardRef<
  React.ElementRef<typeof Avatar>,
  StatusAvatarProps
>(({ src, fallback, status = 'available', hideStatus = false, size = 'md', className, ...props }, ref) => {
  const dotSize = dotSizeClasses[size];

  const renderDot = () => {
    if (hideStatus) return null;

    const baseClasses = cn(
      'absolute border-2 border-background rounded-full flex items-center justify-center',
      dotSize,
    );

    switch (status) {
      case 'available':
        return <span className={cn(baseClasses, 'bg-emerald-500')} />;

      case 'busy':
        return <span className={cn(baseClasses, 'bg-red-500')} />;

      case 'dnd':
        return (
          <span className={cn(baseClasses, 'bg-red-600')}>
            <Ban className={cn(dndIconSize[size], 'text-white')} strokeWidth={3} />
          </span>
        );

      case 'offline':
        return (
          <span className={cn(baseClasses, 'bg-white')}>
            <X
              className={cn(dndIconSize[size], 'text-black')}
              strokeWidth={3}
            />
          </span>
        );

      case 'absent':
        return (
          <span
            className={cn(
              'absolute border-2 border-background rounded-sm flex items-center justify-center shadow-sm',
              size === 'sm' ? 'h-3.5 w-3.5 -bottom-1 -right-1 bg-red-600' : size === 'md' ? 'h-4 w-4 -bottom-1 -right-1 bg-red-600' : 'h-4.5 w-4.5 -bottom-1 -right-1 bg-red-600',
            )}
          >
            <span className={cn('absolute bg-white rounded-full rotate-45', size === 'sm' ? 'h-[1px] w-2' : 'h-[1.5px] w-2.5')} />
            <span className={cn('absolute bg-white rounded-full -rotate-45', size === 'sm' ? 'h-[1px] w-2' : 'h-[1.5px] w-2.5')} />
          </span>
        );

      default:
        return null;
    }
  };

  return (
    <Tooltip>
      <TooltipTrigger asChild>
        <div className="relative inline-flex shrink-0">
          <Avatar ref={ref} className={cn(sizeClasses[size], className)} {...props}>
            <AvatarImage src={src || undefined} />
            <AvatarFallback className={cn(size === 'sm' && 'text-xs', size === 'lg' && 'text-base')}>
              {fallback || '?'}
            </AvatarFallback>
          </Avatar>
          {renderDot()}
        </div>
      </TooltipTrigger>
      {!hideStatus && (
        <TooltipContent side="bottom" className="text-xs">
          {statusLabels[status]}
        </TooltipContent>
      )}
    </Tooltip>
  );
});

StatusAvatar.displayName = 'StatusAvatar';

export { StatusAvatar, statusLabels };
